import { defineStore } from 'pinia';
import { setCssVar } from 'quasar';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import {
  PropertyGroupConfigurationDTO,
  PropertyThemeDTO,
  PropertyGroupPropertyDTO,
  PropertyGroupAreaDTO,
} from 'src/services/api/data-contracts';
import { IImageData } from 'src/composables/utils';

import useUtils from 'src/composables/utils';

const propertyGroupApiService = Services.getService(Api);

export const usePropertyGroupStore = defineStore('propertyGroupStore', {
  state: () => ({
    propertyGroupId: undefined as number | undefined,
    propertyGroupCode: undefined as string | undefined,
    propertyGroupName: undefined as string | undefined,
    propertyGroupLogo: undefined as string | undefined,
    propertyGroupLogoWidth: '100px' as string,

    websiteUrl: '' as string,

    properties: [] as PropertyGroupPropertyDTO[],
    areas: [] as PropertyGroupAreaDTO[],

    propertyGroupImageUrl: null as string,

    theme: undefined as PropertyThemeDTO | undefined,
    enableChildrenSearch: true as boolean,
    enableInfantSearch: true as boolean,
    defaultOccupancyAdult: 1 as number,
    defaultOccupancyChildren: 0 as number,
    defaultOccupancyInfant: 0 as number,
    adultMaximumOccupancy: null as number | null,
    childMaximumOccupancy: null as number | null,

    occupancyAdultLabelText: undefined as string | undefined,
    occupancyChildrenLabelText: undefined as string | undefined,
    occupancyInfantLabelText: undefined as string | undefined,

    hidePromoCodeOnLandingPage: false as boolean,
    useAreaSearch: false as boolean,

    config: undefined as PropertyGroupConfigurationDTO | undefined,
  }),
  getters: {
    getConfig(): PropertyGroupConfigurationDTO | undefined {
      return this.config;
    },
  },
  actions: {
    async getPropertyGroup(propertyGroupCodeOrId) {
      const propertyConfigData = await propertyGroupApiService.propertyGroupGet(
        propertyGroupCodeOrId,
      );

      this.setPropertyGroupData(propertyConfigData.data);
    },
    async setPropertyGroupData(
      propertyConfigData: PropertyGroupConfigurationDTO,
    ) {
      this.propertyGroupId = propertyConfigData.PropertyGroupId;
      this.propertyGroupCode = propertyConfigData.PropertyGroupCode;
      this.propertyGroupName = propertyConfigData.PropertyGroupName;
      this.propertyGroupLogo = propertyConfigData.LogoFileUrl;

      useUtils()
        .getImageDimensions(this.propertyGroupLogo)
        .then((imageData: IImageData) => {
          if (imageData.ratio > 16 / 9) {
            this.propertyGroupLogoWidth = '300px';
          }
        })
        .catch(() => {
          // no op if no logo file is found.
        });

      this.websiteUrl = propertyConfigData.WebsiteUrl;
      this.areas = propertyConfigData.Areas;
      this.properties = propertyConfigData.Properties;

      this.hidePromoCodeOnLandingPage =
        propertyConfigData.HidePromoCodeOnLandingPage;

      this.useAreaSearch = propertyConfigData.UseAreaSearch;
      this.areas = propertyConfigData.Areas;
      this.propertyGroupImageUrl = propertyConfigData.PropertyGroupImageUrl;

      this.configureTheme(propertyConfigData.Theme);
    },
    configureTheme(theme: PropertyThemeDTO) {
      this.theme = theme;
      const utils = useUtils();

      if (this.theme) {
        setCssVar('primary', theme.Primary);
        setCssVar('secondary', theme.Secondary);
        setCssVar('accent', theme.Accent);
        setCssVar('tertiary', theme.Tertiary);
        setCssVar('dark', theme.Dark);
        setCssVar('dark-page', theme.DarkPage);
        setCssVar('positive', theme.Positive);
        setCssVar('negative', theme.Negative);
        setCssVar('info', theme.Info);
        setCssVar('warning', theme.Warning);

        setCssVar('light-page', theme.LightPage);
        setCssVar('brand-primary', theme.BrandPrimary);

        if (utils.isLightColor(theme.BrandPrimary)) {
          setCssVar('text-on-brand-primary', '#000000');
        } else if (!utils.isLightColor(theme.BrandPrimary)) {
          setCssVar('text-on-brand-primary', '#FFFFFF');
        }
      }
    },
  },
});
